import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  CategoryInterface,
  ExtendedAddonInterface,
  ProviderInterface,
  ServiceInterface,
  ServiceType,
  ServiceTypeVariant
} from 'types';

export type ServicesStore = {
  isLoading: boolean;
  isBookingLoading: boolean;
  isAddonsLoading: boolean;
  isLoadingBookServicesByParameters: boolean;
  isProvidersLoading: boolean;
  isServicesLoading: boolean;
  errorMsg: string;
  services: Record<number, Record<number, ServiceInterface[]>>;
  servicesByType: Record<
    number,
    Record<ServiceTypeVariant, ServiceInterface[]>
  >;
  addons: Record<number, Record<number, ExtendedAddonInterface[]>>;
  providers: Record<number, Record<number, ProviderInterface[]>>;
  categories: Record<number, CategoryInterface[]>;
};

const initialState: ServicesStore = {
  isLoading: false,
  isBookingLoading: false,
  isAddonsLoading: false,
  isLoadingBookServicesByParameters: false,
  isProvidersLoading: false,
  isServicesLoading: false,
  errorMsg: '',
  services: {},
  servicesByType: {},
  providers: {},
  addons: {},
  categories: {}
};

export const servicesSlice = createSlice({
  name: 'services',
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsServicesLoading: (state, action: PayloadAction<boolean>) => {
      state.isServicesLoading = action.payload;
    },
    setIsAddonsLoading: (state, action: PayloadAction<boolean>) => {
      state.isAddonsLoading = action.payload;
    },
    setIsProvidersLoading: (state, action: PayloadAction<boolean>) => {
      state.isProvidersLoading = action.payload;
    },
    setIsBookingLoading: (state, action: PayloadAction<boolean>) => {
      state.isBookingLoading = action.payload;
    },
    setIsLoadingBookServicesByParameters: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isLoadingBookServicesByParameters = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.errorMsg = action.payload;
    },
    setCategories: (
      state,
      action: PayloadAction<{
        locationId: number;
        categories: CategoryInterface[];
      }>
    ) => {
      const { locationId, categories } = action.payload;
      state.categories[locationId] = categories;
    },
    setServices: (
      state,
      action: PayloadAction<{
        locationId: number;
        categoryId: number;
        services: ServiceInterface[];
      }>
    ) => {
      const { services, categoryId, locationId } = action.payload;
      if (!state.services[locationId]) {
        state.services[locationId] = {};
      }
      state.services[locationId][categoryId] = services;
    },
    setServicesByType: (
      state,
      action: PayloadAction<{
        locationId: number;
        type: ServiceTypeVariant;
        services: ServiceInterface[];
      }>
    ) => {
      const { services, type, locationId } = action.payload;
      if (!state.servicesByType[locationId]) {
        state.servicesByType[locationId] = {
          [ServiceType.All]: [],
          [ServiceType.Recent]: [],
          [ServiceType.Eligible]: [],
          [ServiceType.Interested]: [],
          [ServiceType.Recommended]: [],
          [ServiceType.Popular]: []
        };
      }
      state.servicesByType[locationId][type] = services;
    },
    setAddonsForService: (
      state,
      action: PayloadAction<{
        locationId: number;
        serviceId: number;
        addons: ExtendedAddonInterface[];
      }>
    ) => {
      const { locationId, serviceId, addons } = action.payload;
      if (!state.addons[locationId]) {
        state.addons[locationId] = {};
      }
      state.addons[locationId][serviceId] = addons;
    },
    setProviders: (
      state,
      action: PayloadAction<{
        locationId: number;
        serviceId: number;
        providers: ProviderInterface[];
      }>
    ) => {
      const { locationId, serviceId, providers } = action.payload;
      if (!state.providers[locationId]) {
        state.providers[locationId] = {};
      }
      state.providers[locationId][serviceId] = providers;
    },
    resetServicesState: () => initialState
  }
});

export const {
  setIsLoading,
  setIsAddonsLoading,
  setError,
  setServices,
  setServicesByType,
  setAddonsForService,
  setProviders,
  setIsProvidersLoading,
  setCategories,
  setIsServicesLoading,
  setIsBookingLoading,
  setIsLoadingBookServicesByParameters,
  resetServicesState
} = servicesSlice.actions;

export default servicesSlice.reducer;
